import { useMemo } from "react";
import { IAircraft, IAircraftType, IFullAircraftConfiguration } from "@vmosolutions/oc-interfaces";
//formik
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikSelectField,
  FormikColorPickField,
  FormikSwitchField,
} from "../../../../MUI5FormikComponents/FormikWrappers";
import { Grid, Typography } from "@mui/material";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { aircraftRegistrationValidation } from "../../../Validation/validation";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import { PolicyResource } from "@vmosolutions/oc-interfaces";
import { AircraftSaveBar } from "../AircraftSaveBar";
import { v1 } from "uuid";
import _ from "lodash";

interface IAircraftProps {
  selectedAircraft?: IAircraft | null;
  updateAircraftList: any;
  aircraftTypeOptions?: IAircraftType[];
  aircraftConfigOptions?: IFullAircraftConfiguration[];
  setActiveModal?: (activeModal: IActiveModal) => void;
  setForm: any;
  setActiveAircraft: any;
  stagedAircraft: IAircraft;
  setStagedAircraft: any;
}

//set initial value when user click "new aircraft"
const initialAircraftValues: IAircraft = {
  _id: undefined,
  foreignRef: [],
  state: undefined, // Strip in update call so it's never passed back
  custom: {},
  registration: "",
  aircraftType: undefined,
  fleetNumber: undefined,
  serialNumber: "",
  division: undefined,
  defaultFlightType: undefined,
  generateErrors: true,
  requireMessages: true,
  subCharter: false,
  planning: false,
  isActive: true,
  defaultConfig: undefined,
  groupColour: undefined,
  identityDisplayOrder: ["registration", "fleetNumber"],
};

type AircraftFormType = FormikProps<IAircraft>;

const numberValidationMsg = "A numberic value is expected.";
// Aircraft Form Validation with YUP
const AircraftSchema = Yup.object().shape({
  registration: Yup.string()
    .matches(aircraftRegistrationValidation, "Please enter a valid registration name")
    .required("Registration is required"),
  aircraftType: Yup.object().required("Aircraft Type is required").nullable(),
  serialNumber: Yup.string().required("Serial Number is required").nullable(),
  division: Yup.number().nullable().typeError(numberValidationMsg),
});

/**
 * Base Aircraft Form
 * @param props - selected aircraft (from ManageAircraft)
 */
export const AircraftForm = (props: IAircraftProps): JSX.Element => {
  const {
    selectedAircraft,
    updateAircraftList,
    aircraftTypeOptions,
    setForm,
    setActiveAircraft,
    aircraftConfigOptions,
    stagedAircraft,
    setStagedAircraft,
  } = props;
  const dispatch = useDispatch();
  const customSchema = useSelector((state: IRootState) => state.customPages?.aircraft);
  const schema = useMemo(() => {
    try {
      const data = customSchema ? JSON.parse(customSchema) : null;
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [customSchema]);

  const customPath = "custom";
  const initialCustomValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    if (!schema || (schema && !schema.hasOwnProperty("properties")) || !selectedAircraft) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] = schema.properties[value].default ?? schema.properties[value]?.selectorProps?.defaultValue;
    });
    return values;
  }, [schema, selectedAircraft]);

  const initialValues = useMemo(
    () => _.merge({ [customPath]: initialCustomValues }, { ...initialAircraftValues, ...selectedAircraft }),
    [selectedAircraft, initialCustomValues, customPath]
  );

  const aircraftTypeFilter = (
    option: { label: string; value: string; data: IAircraftType },
    rawInput: string
  ): boolean => {
    if (!rawInput || rawInput === "") {
      return true;
    }

    if (rawInput) {
      const acType = option.data;
      const searchTerms = [
        acType.title.toUpperCase(),
        (acType.description ?? "").toUpperCase(),
        acType.codeIATA,
        acType.codeICAO,
      ];
      return searchTerms.some((x) => x.includes(rawInput.toUpperCase()));
    }
    return false;
  };

  const updateAircraft = async (values: IAircraft, helpers: FormikHelpers<IAircraft>): Promise<void> => {
    const { setSubmitting, setStatus } = helpers;
    if (stagedAircraft) {
      setStatus({ success: true });
      updateAircraftList(values);
      setStagedAircraft(values);
      setActiveAircraft(values);
    } else {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Aircraft...",
        successMessage: "Aircraft Successfully Updated.",
        errorMessage: "Error: Unable to update Aircraft",
      };
      const { state, ...payload } = values;
      const response = await AmplifyAPIWrapper.Put<IAircraft>(`/aircraft/${values._id}`, payload, notifications);
      if (response?._id) {
        setStatus({ success: true });
        updateAircraftList(response);
        setActiveAircraft(response);
      } else {
        setStatus({ success: false });
      }
    }
    setSubmitting(false);
  };

  const createAircraft = (values: IAircraft): void => {
    const { state, ...payload } = values;
    payload._id = `temp_${v1()}`;
    setStagedAircraft(payload);
    setForm("config");
  };

  const handleAircraftSubmit = (values: IAircraft, helpers: FormikHelpers<IAircraft>): void => {
    if (values._id !== undefined) {
      updateAircraft(values, helpers);
    } else {
      createAircraft(values);
    }
  };

  //The display order options for the aircraft row
  const IdentifierOptions = [
    { value: ["registration", "fleetNumber"], label: "Registration - Fleet Number" },
    { value: ["fleetNumber", "registration"], label: "Fleet Number - Registration" },
    { value: ["aircraftType.title", "registration"], label: "Aircraft Type - Registration" },
    { value: ["registration", "aircraftType.title"], label: "Registration - Aircraft Type" },
    { value: ["aircraftType.title", "fleetNumber"], label: "Aircraft Type - Fleet Number" },
    { value: ["fleetNumber", "aircraftType.title"], label: "Fleet Number - Aircraft Type" },
  ];

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleAircraftSubmit}
      validationSchema={AircraftSchema}
    >
      {(aircraftForm: AircraftFormType) => {
        const { dirty, isValid, isSubmitting, handleSubmit } = aircraftForm;

        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AircraftSaveBar
                  aircraft={selectedAircraft}
                  saveAirCraft={handleSubmit}
                  mode={"aircraft"}
                  disabled={!isValid || isSubmitting}
                  hasError={!isValid}
                  stagedAircraft={stagedAircraft}
                  dirty={dirty}
                />
              </Grid>
              <Grid
                xs={12}
                item
                container
                columnSpacing={2}
                sx={{ overflowY: "auto", maxHeight: "calc(100svh - 290px)" }}
              >
                <Grid item xs={4}>
                  <Typography variant="subtitle2">Required Fields</Typography>
                  <FormikTextField
                    label="Registration"
                    name="registration"
                    placeholder="example: C-ABCD, C-XYZA, etc."
                    inputProps={{ maxLength: 8 }}
                  />
                  <FormikSelectField
                    name="aircraftType"
                    label="Aircraft Type"
                    isClearable
                    options={aircraftTypeOptions}
                    getOptionLabel={(option: IAircraftType) =>
                      `${option.description} ${option.description ? `${option.title} - (${option.codeICAO})` : ""}`
                    }
                    getOptionValue={(option: IAircraftType) => option._id}
                  />
                  {selectedAircraft && (
                    <FormikSelectField
                      className="field selectfield default_configuration"
                      name="defaultConfig"
                      label="Default Configuration"
                      getOptionLabel={(option: IFullAircraftConfiguration) => option.title}
                      getOptionValue={(option: IFullAircraftConfiguration) => option._id}
                      options={aircraftConfigOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isSearchable={true}
                      isClearable={false}
                      menuPosition="fixed"
                      hideSelectedValues={true}
                      disabled={!selectedAircraft}
                      placeholder="example: 12345"
                    />
                  )}
                  <FormikTextField label="Serial Number" name="serialNumber" placeholder="example: 12345" />
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="subtitle2">Optional Fields</Typography>
                  <FormikTextField label="Fleet Number" name="fleetNumber" placeholder="example: 42, 57" />
                  <FormikSelectField
                    name="identityDisplayOrder"
                    label="Identifier Display Order"
                    parse={(option: any) => option.value}
                    format={(value: any) => {
                      return IdentifierOptions.find(
                        (option: any) => JSON.stringify(option.value) === JSON.stringify(value)
                      );
                    }}
                    options={IdentifierOptions}
                  />
                  <FormikColorPickField label="Colour" name="groupColour" />
                  <FormikTextField label="Division" name="division" placeholder="example: 12" />
                  <Grid item sx={{}}>
                    <FormikSwitchField label="Generate Errors" name="generateErrors" />
                    <FormikSwitchField label="Require Messages" name="requireMessages" />
                    <FormikSwitchField label="Sub Charter" name="subCharter" />
                    <FormikSwitchField label="Planning" name="planning" />
                    <FormikSwitchField label="Active" name="isActive" />
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2">Custom Fields</Typography>
                  <CustomFieldsV2
                    schema={schema}
                    objectPath={customPath}
                    resource={PolicyResource.Aircraft}
                    displayHeader={false}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
